import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Stack,
  Paper,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Star, WorkspacePremium, Timer } from '@mui/icons-material';
import { fetchWithAuth } from '../lib/api';
import { useOrganization } from '../providers/OrganizationProvider';

function PlanOptionCard({ title, price, period, description, selected, onClick, badge }) {
  return (
    <Paper
      onClick={onClick}
      sx={{
        p: 2,
        cursor: 'pointer',
        borderRadius: 2.5,
        border: '2px solid',
        borderColor: selected ? 'primary.main' : 'rgba(0,0,0,0.08)',
        bgcolor: selected ? 'rgba(0,128,96,0.04)' : '#fff',
        transition: 'all 0.2s',
        position: 'relative',
      }}
    >
      {badge && (
        <Chip
          label={badge}
          size="small"
          sx={{ position: 'absolute', top: 10, right: 10, fontWeight: 700 }}
          color="primary"
        />
      )}
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{title}</Typography>
      <Typography variant="h5" sx={{ fontWeight: 800, color: 'primary.main', my: 0.5 }}>
        {price}
        <Typography component="span" variant="body2" color="text.secondary">{period}</Typography>
      </Typography>
      <Typography variant="body2" color="text.secondary">{description}</Typography>
    </Paper>
  );
}

export default function PlanLimitModal({
  open,
  onClose,
  mode = 'employee_limit',
  featureName,
  maxActiveEmployees,
  currentCount,
  canStartTrial = true,
  effectivePlan = 'free',
  demoMode = false,
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { refetchOrganizations } = useOrganization();
  const [selectedPlan, setSelectedPlan] = useState('pro');
  const [action, setAction] = useState(canStartTrial ? 'trial' : 'upgrade');

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['subscription'] });
    refetchOrganizations();
  };

  const trialMutation = useMutation({
    mutationFn: async (trialPlan) => {
      const res = await fetchWithAuth('/api/billing/start-trial', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ trialPlan }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Failed to start trial');
      }
      return res.json();
    },
    onSuccess: () => {
      invalidate();
      onClose();
    },
  });

  const demoUpgradeMutation = useMutation({
    mutationFn: async (plan) => {
      const res = await fetchWithAuth('/api/billing/demo-upgrade', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Failed to upgrade');
      }
      return res.json();
    },
    onSuccess: () => {
      invalidate();
      onClose();
    },
  });

  const isPending = trialMutation.isPending || demoUpgradeMutation.isPending;
  const error = trialMutation.error || demoUpgradeMutation.error;

  const handlePrimary = () => {
    if (action === 'trial') {
      trialMutation.mutate(selectedPlan);
    } else if (demoMode) {
      demoUpgradeMutation.mutate(selectedPlan);
    } else {
      navigate('/settings/billing');
      onClose();
    }
  };

  const planLabel = effectivePlan === 'business' ? 'Business' : effectivePlan === 'pro' ? 'Pro' : 'Free';

  const title = mode === 'employee_limit'
    ? 'Ξεπεράσατε το όριο υπαλλήλων'
    : 'Αναβάθμιση απαιτείται';

  const subtitle = mode === 'employee_limit'
    ? effectivePlan === 'business'
      ? `Έχετε ${currentCount} ενεργούς υπαλλήλους. Ελέγξτε τη συνδρομή σας — το Business plan επιτρέπει απεριόριστους ενεργούς.`
      : `Το ${planLabel} plan επιτρέπει έως ${maxActiveEmployees} ενεργούς υπαλλήλους. Τώρα έχετε ${currentCount}.`
    : `Η λειτουργία «${featureName || 'αυτή'}» δεν είναι διαθέσιμη στο Free plan.`;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ fontWeight: 800 }}>{title}</DialogTitle>
      <DialogContent>
        <Typography color="text.secondary" sx={{ mb: 2 }}>{subtitle}</Typography>

        {canStartTrial && (
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <Button
              variant={action === 'trial' ? 'contained' : 'outlined'}
              onClick={() => setAction('trial')}
              startIcon={<Timer />}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
            >
              Δοκιμή 14 ημερών
            </Button>
            <Button
              variant={action === 'upgrade' ? 'contained' : 'outlined'}
              onClick={() => setAction('upgrade')}
              startIcon={<Star />}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
            >
              Αναβάθμιση
            </Button>
          </Stack>
        )}

        {(action === 'trial' || !canStartTrial) && (
          <Stack spacing={1.5}>
            <PlanOptionCard
              title="Pro"
              price="€9,90"
              period="/μήνα"
              description="Έως 10 ενεργοί υπάλληλοι • Όλα τα features"
              selected={selectedPlan === 'pro'}
              onClick={() => setSelectedPlan('pro')}
              badge={action === 'trial' ? '14 ημέρες δωρεάν' : null}
            />
            <PlanOptionCard
              title="Business"
              price="€19"
              period="/μήνα"
              description="Απεριόριστοι ενεργοί υπάλληλοι • Όλα τα features"
              selected={selectedPlan === 'business'}
              onClick={() => setSelectedPlan('business')}
              icon={<WorkspacePremium />}
              badge={action === 'trial' ? '14 ημέρες δωρεάν' : null}
            />
          </Stack>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
            {error.message}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Άκυρο</Button>
        {action === 'trial' && canStartTrial ? (
          <Button
            variant="contained"
            onClick={handlePrimary}
            disabled={isPending}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 3 }}
          >
            {isPending ? <CircularProgress size={20} color="inherit" /> : `Ξεκίνα δοκιμή ${selectedPlan === 'business' ? 'Business' : 'Pro'}`}
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handlePrimary}
            disabled={isPending}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 3 }}
          >
            {isPending ? (
              <CircularProgress size={20} color="inherit" />
            ) : demoMode ? (
              `Αναβάθμιση σε ${selectedPlan === 'business' ? 'Business' : 'Pro'}`
            ) : (
              'Δες τα πλάνα'
            )}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
